/**
 * NOLAS 2026 — © Mika (Mikaël FOURNY).
 * src/pwa/updateBanner.ts
 *
 * Rôle :
 *   Bandeau « nouvelle version disponible », NON bloquant, affiché quand le
 *   Service Worker a préparé une mise à jour.
 *
 *   🔴 Pourquoi ce fichier existe (Mika, 2026-09-09) : « je veux une zone de
 *   recherche dans le menu admin » — alors qu'elle était livrée et EN LIGNE
 *   depuis le 30/08, vérifiée dans le fragment réellement servi. Ce n'était
 *   donc pas la fonction qui manquait, c'est la MISE À JOUR qui n'arrivait
 *   pas jusqu'à l'écran.
 *
 *   La cause : la mise à jour était annoncée par un `window.confirm()` natif.
 *   Une boîte modale bloquante, sans style, qui interrompt en plein travail —
 *   on la ferme d'un réflexe, et l'on reste sur l'ancienne version SANS
 *   AUCUNE trace de ce qu'on vient de refuser. Le refus était définitif pour
 *   la session, et rien ne le rappelait ensuite.
 *
 * Le choix retenu, et pourquoi PAS un rechargement automatique :
 *   Recharger tout seul ferait perdre une fiche exposant à moitié remplie.
 *   Le bandeau attend donc un geste — mais il RESTE visible tant qu'on ne
 *   l'a pas fait. Non bloquant et persistant : l'inverse exact d'un
 *   `confirm()`, qui est bloquant et éphémère.
 *
 * Réutilisable :
 *   DOM natif, aucune dépendance à React ni au routeur : ce module est appelé
 *   avant le montage de l'application. Copiable tel quel dans une autre PWA.
 *
 * @author Mika
 * @since  2026-09-09
 */

/** Identifiant du bandeau — sert aussi de garde anti-doublon. */
const ID_BANDEAU = 'nolas-maj-disponible';

/**
 * Affiche le bandeau de mise à jour.
 *
 * @param surMiseAJour Action déclenchée par le bouton (recharge l'app).
 */
export function afficherBandeauMiseAJour(surMiseAJour: () => void): void {
  // Le Service Worker peut signaler la même mise à jour plusieurs fois
  // (une par contrôleur). Sans cette garde, les bandeaux s'empileraient.
  if (document.getElementById(ID_BANDEAU) !== null) {
    return;
  }

  const barre = document.createElement('div');
  barre.id = ID_BANDEAU;
  barre.setAttribute('role', 'status');
  barre.setAttribute('aria-live', 'polite');
  barre.style.cssText = [
    'position:fixed', 'left:0', 'right:0', 'bottom:0', 'z-index:2147483000',
    'display:flex', 'flex-wrap:wrap', 'gap:0.75rem',
    'align-items:center', 'justify-content:center',
    'padding:0.85rem 1rem',
    'background:#13244E', 'color:#FFFFFF',
    'font:600 0.95rem/1.4 system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
    'box-shadow:0 -4px 18px rgba(0,0,0,0.28)',
  ].join(';');

  const texte = document.createElement('span');
  texte.textContent = 'Une nouvelle version de l’application est prête.';

  const bouton = document.createElement('button');
  bouton.type = 'button';
  bouton.textContent = 'Mettre à jour';
  bouton.style.cssText = [
    'padding:0.45rem 1.1rem', 'border:none', 'border-radius:8px',
    'background:#F2641E', 'color:#FFFFFF', 'cursor:pointer',
    'font:700 0.95rem/1.2 inherit',
  ].join(';');
  bouton.addEventListener('click', () => {
    bouton.disabled = true;
    bouton.textContent = 'Mise à jour…';
    surMiseAJour();
  });

  // Volontairement PAS de bouton « plus tard » : c'est exactement le geste
  // qui a fait rester Mika sur une version ancienne pendant dix jours.
  // Le bandeau ne masque qu'une bande en bas de page ; on peut continuer à
  // travailler sans le fermer.
  barre.append(texte, bouton);
  document.body.appendChild(barre);
}
